import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  Crown,
  Eye,
  Edit,
  Shield,
  Package,
  FileText,
  UserCheck,
  UserX,
  BookUser,
  FilePlus,
  Building2,
  Briefcase,
  ChevronRight,
  Clock,
  Pencil,
} from "lucide-react";
import { MODULE_SECTIONS, ALL_MODULE_KEYS, DEFAULT_MODULES, DEFAULT_MODULES_EDIT } from "@/utils/permissions";

const ROLES = [
  { value: "admin", label: "Administrador", icon: Crown, color: "bg-amber-500", desc: "Ve todo. Puede editar y borrar." },
  { value: "supervisor", label: "Supervisor", icon: Eye, color: "bg-blue-500", desc: "Ve módulos asignados. Edita según permisos por módulo." },
  { value: "ejecutivo", label: "Ejecutivo", icon: Edit, color: "bg-slate-500", desc: "Ve módulos asignados. Edita según permisos por módulo." },
];

const SECTION_ICONS = {
  cotizaciones: FilePlus,
  clientes: Building2,
  logistica: Package,
  traveldesk: FileText,
  administracion: Briefcase,
  configuracion_section: Shield,
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    role: "ejecutivo",
    is_active: true,
    modules: { ...DEFAULT_MODULES },
    modules_edit: { ...DEFAULT_MODULES_EDIT },
    departamento_operativo: "",
    ver_actividad_departamento: false,
    ver_actividad_empresa: false,
  });
  const [departamentos, setDepartamentos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then(({ data }) => setDepartamentos(data.departamentos_operativos || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Solo administradores pueden ver usuarios");
      } else {
        toast.error("Error al cargar usuarios");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    const userModules = { ...DEFAULT_MODULES, ...(user.modules || {}) };
    const userModulesEdit = { ...DEFAULT_MODULES_EDIT, ...(user.modules_edit || {}) };
    setFormData({
      role: user.role || "ejecutivo",
      is_active: user.is_active !== false,
      modules: userModules,
      modules_edit: userModulesEdit,
      departamento_operativo: user.departamento_operativo || "",
      ver_actividad_departamento: !!user.ver_actividad_departamento,
      ver_actividad_empresa: !!user.ver_actividad_empresa,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/users/${editingUser.id}`, formData, { withCredentials: true });
      toast.success("Usuario actualizado");
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const toggleSection = (sectionKey, value) => {
    const section = MODULE_SECTIONS.find(s => s.key === sectionKey);
    if (!section) return;
    const updated = { ...formData.modules };
    section.children.forEach(c => { updated[c.key] = value; });
    setFormData({ ...formData, modules: updated });
  };

  const toggleSectionEdit = (sectionKey, value) => {
    const section = MODULE_SECTIONS.find(s => s.key === sectionKey);
    if (!section) return;
    const updated = { ...formData.modules_edit };
    section.children.forEach(c => { updated[c.key] = value; });
    setFormData({ ...formData, modules_edit: updated });
  };

  const toggleModule = (moduleKey, value) => {
    const newModules = { ...formData.modules, [moduleKey]: value };
    const newModulesEdit = { ...formData.modules_edit };
    // If disabling visibility, also disable edit
    if (!value) newModulesEdit[moduleKey] = false;
    setFormData({ ...formData, modules: newModules, modules_edit: newModulesEdit });
  };

  const toggleModuleEdit = (moduleKey, value) => {
    setFormData({
      ...formData,
      modules_edit: { ...formData.modules_edit, [moduleKey]: value },
    });
  };

  const isSectionAllChecked = (sectionKey) => {
    const section = MODULE_SECTIONS.find(s => s.key === sectionKey);
    if (!section) return false;
    return section.children.every(c => formData.modules[c.key]);
  };

  const isSectionPartial = (sectionKey) => {
    const section = MODULE_SECTIONS.find(s => s.key === sectionKey);
    if (!section) return false;
    const checked = section.children.filter(c => formData.modules[c.key]).length;
    return checked > 0 && checked < section.children.length;
  };

  const isSectionEditAllChecked = (sectionKey) => {
    const section = MODULE_SECTIONS.find(s => s.key === sectionKey);
    if (!section) return false;
    return section.children.every(c => formData.modules_edit[c.key]);
  };

  const isSectionEditPartial = (sectionKey) => {
    const section = MODULE_SECTIONS.find(s => s.key === sectionKey);
    if (!section) return false;
    const checked = section.children.filter(c => formData.modules_edit[c.key]).length;
    return checked > 0 && checked < section.children.length;
  };

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[2];

  const getActiveModuleLabels = (user) => {
    if (user.role === "admin") return ["Todos"];
    const mods = user.modules || {};
    const labels = [];
    MODULE_SECTIONS.forEach(section => {
      const activeChildren = section.children.filter(c => mods[c.key]);
      if (activeChildren.length === section.children.length) {
        labels.push(section.label);
      } else {
        activeChildren.forEach(c => labels.push(c.label));
      }
    });
    return labels.length > 0 ? labels : ["Ninguno"];
  };

  const getEditModuleCount = (user) => {
    if (user.role === "admin") return "Todos";
    const mods = user.modules_edit || {};
    const count = Object.values(mods).filter(Boolean).length;
    return count > 0 ? `${count} módulos` : "Ninguno";
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="user-management-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gestión de Usuarios</h1>
          <p className="text-slate-500">Administra roles, acceso y permisos de edición por módulo</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg"><Users className="w-5 h-5 text-slate-600" /></div>
                <div><p className="text-2xl font-bold">{users.length}</p><p className="text-xs text-slate-500">Total Usuarios</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className={users.filter(u => u.pending_approval).length > 0 ? "border-amber-300 bg-amber-50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 bg-opacity-20 rounded-lg"><Clock className="w-5 h-5 text-amber-500" /></div>
                <div><p className="text-2xl font-bold">{users.filter(u => u.pending_approval).length}</p><p className="text-xs text-slate-500">Pendientes</p></div>
              </div>
            </CardContent>
          </Card>
          {ROLES.map(role => (
            <Card key={role.value}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${role.color} bg-opacity-20 rounded-lg`}>
                    <role.icon className={`w-5 h-5 ${role.color.replace("bg-", "text-")}`} />
                  </div>
                  <div><p className="text-2xl font-bold">{users.filter(u => u.role === role.value).length}</p><p className="text-xs text-slate-500">{role.label}s</p></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Role Legend */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {ROLES.map(r => (
                <div key={r.value} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                  <Badge className={`${r.color} text-white mt-0.5`}>{r.label}</Badge>
                  <p className="text-sm text-slate-600">{r.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader><CardTitle>Usuarios del Sistema</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>USUARIO</TableHead>
                  <TableHead>EMAIL</TableHead>
                  <TableHead>ROL</TableHead>
                  <TableHead>MÓDULOS</TableHead>
                  <TableHead>PUEDE EDITAR</TableHead>
                  <TableHead>ESTADO</TableHead>
                  <TableHead className="text-right">ACCIONES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10">Cargando...</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No hay usuarios</TableCell></TableRow>
                ) : (
                  [...users].sort((a, b) => {
                    if (a.pending_approval && !b.pending_approval) return -1;
                    if (!a.pending_approval && b.pending_approval) return 1;
                    return 0;
                  }).map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const moduleLabels = getActiveModuleLabels(user);
                    const editCount = getEditModuleCount(user);

                    return (
                      <TableRow key={user.id} className={!user.is_active ? "bg-slate-50 opacity-60" : ""} data-testid={`user-row-${user.email}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center"><Users className="w-4 h-4 text-slate-500" /></div>
                            )}
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={`${roleInfo.color} text-white`}>{roleInfo.label}</Badge>
                          {user.departamento_operativo && (
                            <p className="text-[11px] text-slate-400 mt-1">{user.departamento_operativo}{user.ver_actividad_empresa ? " · 👁 empresa" : user.ver_actividad_departamento ? " · 👁 depto" : ""}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {moduleLabels.slice(0, 3).map((label, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{label}</Badge>
                            ))}
                            {moduleLabels.length > 3 && <Badge variant="outline" className="text-xs">+{moduleLabels.length - 3}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${editCount !== "Ninguno" ? "border-green-300 text-green-700 bg-green-50" : ""}`}>
                            <Pencil className="w-3 h-3 mr-1" />{editCount}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.is_active !== false ? (
                            <Badge className="bg-green-500 text-white"><UserCheck className="w-3 h-3 mr-1" /> Activo</Badge>
                          ) : user.pending_approval ? (
                            <Badge className="bg-amber-500 text-white"><Clock className="w-3 h-3 mr-1" /> Pendiente</Badge>
                          ) : (
                            <Badge className="bg-red-500 text-white"><UserX className="w-3 h-3 mr-1" /> Inactivo</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(user)} disabled={user.id === currentUser?.id} data-testid={`edit-user-${user.email}`}>
                            <Edit className="w-4 h-4 mr-1" /> Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {editingUser?.picture && <img src={editingUser.picture} alt="" className="w-8 h-8 rounded-full" />}
                Editar: {editingUser?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Role */}
              <div className="space-y-2">
                <Label className="font-semibold">Rol del Usuario</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="role-select-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        <div className="flex items-center gap-2"><r.icon className="w-4 h-4" /><span>{r.label}</span></div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">{getRoleInfo(formData.role).desc}</p>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <Label className="font-semibold">Usuario Activo</Label>
                <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} data-testid="user-active-switch" />
              </div>

              {/* Departamento operativo y visibilidad */}
              <div className="space-y-3 p-3 border rounded-lg">
                <div className="space-y-2">
                  <Label className="font-semibold">Departamento operativo</Label>
                  <Select value={formData.departamento_operativo || "__ninguno__"}
                    onValueChange={(v) => setFormData({ ...formData, departamento_operativo: v === "__ninguno__" ? "" : v })}>
                    <SelectTrigger data-testid="user-departamento-select"><SelectValue placeholder="Sin departamento" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ninguno__">Sin departamento</SelectItem>
                      {departamentos.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Se alimenta de la tabla de Departamentos operativos (Configuración → Tablas).</p>
                </div>
                {formData.role !== "admin" && (
                  <div className="space-y-2">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <Checkbox checked={formData.ver_actividad_departamento}
                        onCheckedChange={(v) => setFormData({ ...formData, ver_actividad_departamento: !!v })}
                        className="mt-0.5" data-testid="user-check-ver-departamento" />
                      <span className="text-sm text-slate-600 leading-snug">
                        <b>Ver actividad de otros usuarios del departamento</b>
                        <span className="block text-xs text-slate-400">Podrá ver (solo lectura) las cotizaciones registradas por sus compañeros del mismo departamento operativo. Las suyas sí las puede editar.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <Checkbox checked={formData.ver_actividad_empresa}
                        onCheckedChange={(v) => setFormData({ ...formData, ver_actividad_empresa: !!v })}
                        className="mt-0.5" data-testid="user-check-ver-empresa" />
                      <span className="text-sm text-slate-600 leading-snug">
                        <b>Ver actividad de toda la empresa</b>
                        <span className="block text-xs text-slate-400">Podrá ver (solo lectura, sin editar) las cotizaciones de todos los usuarios de todos los departamentos. Si está activo, tiene prioridad sobre el check de departamento.</span>
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Module Permissions - only for non-admin */}
              {formData.role !== "admin" ? (
                <div className="space-y-3">
                  <div>
                    <Label className="font-semibold">Acceso y Permisos por Módulo</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      <strong>Ver</strong> = puede acceder al módulo. <strong>Editar</strong> = puede crear, modificar y guardar cambios en ese módulo. Solo Admin puede eliminar registros.
                    </p>
                  </div>

                  <div className="border rounded-lg divide-y">
                    {MODULE_SECTIONS.map(section => {
                      const SectionIcon = SECTION_ICONS[section.key] || Package;
                      const allChecked = isSectionAllChecked(section.key);
                      const partial = isSectionPartial(section.key);
                      const allEditChecked = isSectionEditAllChecked(section.key);
                      const editPartial = isSectionEditPartial(section.key);

                      return (
                        <div key={section.key} data-testid={`module-section-${section.key}`}>
                          {/* Section Header */}
                          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                            <div className="flex items-center gap-2 flex-1">
                              <SectionIcon className="w-4 h-4 text-slate-600" />
                              <span className="font-medium text-sm">{section.label}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <div className="flex items-center gap-1.5">
                                <Checkbox
                                  checked={allChecked ? true : partial ? "indeterminate" : false}
                                  onCheckedChange={(v) => toggleSection(section.key, !!v)}
                                  data-testid={`section-toggle-${section.key}`}
                                />
                                <span>Ver</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Checkbox
                                  checked={allEditChecked ? true : editPartial ? "indeterminate" : false}
                                  onCheckedChange={(v) => toggleSectionEdit(section.key, !!v)}
                                  data-testid={`section-edit-toggle-${section.key}`}
                                />
                                <span>Editar</span>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {section.children.filter(c => formData.modules[c.key]).length}/{section.children.length}
                              </Badge>
                            </div>
                          </div>
                          {/* Individual module items */}
                          <div className="px-4 py-2 space-y-1">
                            {section.children.map(child => (
                              <div key={child.key} className="flex items-center gap-3 py-1.5 pl-6">
                                <span className="text-sm text-slate-700 flex-1">{child.label}</span>
                                <div className="flex items-center gap-4 text-xs text-slate-500">
                                  <div className="flex items-center gap-1.5 w-14">
                                    <Checkbox
                                      checked={formData.modules[child.key] || false}
                                      onCheckedChange={(v) => toggleModule(child.key, !!v)}
                                      data-testid={`module-toggle-${child.key}`}
                                    />
                                    <span>Ver</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 w-16">
                                    <Checkbox
                                      checked={formData.modules_edit[child.key] || false}
                                      onCheckedChange={(v) => toggleModuleEdit(child.key, !!v)}
                                      disabled={!formData.modules[child.key]}
                                      data-testid={`module-edit-toggle-${child.key}`}
                                    />
                                    <span className={!formData.modules[child.key] ? "text-slate-300" : ""}>Editar</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Quick actions */}
                  <div className="flex gap-2 flex-wrap">
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const all = {}; ALL_MODULE_KEYS.forEach(k => { all[k] = true; });
                      setFormData({ ...formData, modules: all });
                    }} data-testid="select-all-modules">Activar Ver Todos</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      setFormData({ ...formData, modules: { ...DEFAULT_MODULES } });
                    }} data-testid="deselect-all-modules">Desactivar Ver Todos</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const all = {}; ALL_MODULE_KEYS.forEach(k => { all[k] = true; });
                      setFormData({ ...formData, modules_edit: all });
                    }} data-testid="select-all-edit">Activar Editar Todos</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      setFormData({ ...formData, modules_edit: { ...DEFAULT_MODULES_EDIT } });
                    }} data-testid="deselect-all-edit">Desactivar Editar Todos</Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-amber-800 text-sm">Acceso Total</span>
                  </div>
                  <p className="text-sm text-amber-700">
                    Los administradores tienen acceso a todos los módulos y pueden editar y borrar cualquier registro.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">Guardar Cambios</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UserManagement;
